import { Link } from '@tanstack/react-router'
import { ChevronRightIcon } from 'lucide-react'
import type { AccommodationDetail } from '@/api/accommodation/types'
import type { LandingPage } from '@/features/landing/types'
import { getBasePages } from '@/features/landing/registry'
import { getComboByParts, isIndexedCombo } from '@/features/landing/combos'
import { LandingLink } from '@/features/landing/components/LandingLink'
import { Typography } from '@/components/ui/typography'
import { unitFullName } from '@/lib/unit'

function regionPage(location: AccommodationDetail): LandingPage | undefined {
  return getBasePages().find((page) => page.kind === 'region' && page.filter.region === location.region.slug)
}

/**
 * Landing pages this location belongs to: its category page plus every
 * facility page whose flag it has (and pets). Dimension pages upgrade to the
 * region×dimension combo when one is inventory-backed, so the links stay as
 * specific as possible.
 */
function relatedPages(location: AccommodationDetail): LandingPage[] {
  const { facility, unit } = location
  const facilityFlags: Record<string, boolean> = {
    sauna: facility.sauna,
    pool: facility.pool,
    whirlpool: facility.whirlpool,
    hotTub: facility.hotTub,
    grill: facility.grill,
    indoorFireplace: unit.facility.general.indoorFireplace,
    childrenPlayground: facility.childrenPlayground,
  }

  const dimensions = getBasePages().filter((page) => {
    if (page.kind === 'category') {
      return page.filter.category === location.category.slug
    }
    if (page.kind !== 'facility') return false
    if (page.filter.pets) return location.conditions.pets
    return page.filter.facility != null && facilityFlags[page.filter.facility]
  })

  const region = regionPage(location)
  return dimensions.map((dimension) => {
    // Only inventory-backed combos are indexed; linking to the rest from the
    // detail pages (the site's strongest internal links) is what pushed thin
    // combos into the index and even into Google's sitelinks.
    if (!region || !isIndexedCombo(region.slug, dimension.slug)) return dimension
    return getComboByParts(region.slug, dimension.slug) ?? dimension
  })
}

/** Visible breadcrumb (Domů › region › name), mirrors the BreadcrumbList JSON-LD. */
export function DetailBreadcrumb({ location }: { location: AccommodationDetail }) {
  const region = regionPage(location)

  return (
    <nav aria-label="Drobečková navigace" className="mb-3 flex flex-wrap items-center gap-1 text-sm text-slate-500">
      <Link to="/" className="hover:text-red hover:underline">
        Domů
      </Link>
      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      {region ? (
        <LandingLink page={region} className="hover:text-red hover:underline">
          {region.linkLabel}
        </LandingLink>
      ) : (
        <Link to="/ubytovani" className="hover:text-red hover:underline">
          Ubytování
        </Link>
      )}
      <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="text-slate-700">{unitFullName(location, location.unit)}</span>
    </nav>
  )
}

/** Chip links from the detail to the landing pages the location belongs to. */
export function DetailLandingLinks({ location }: { location: AccommodationDetail }) {
  const region = regionPage(location)
  const pages = relatedPages(location)
  const chips = region ? [region, ...pages] : pages

  if (chips.length === 0) return null

  return (
    <div id="landing-links">
      <Typography variant="h2">Objevte podobné pobyty</Typography>
      <div className="mt-4 flex flex-wrap gap-2">
        {chips.map((page) => (
          <LandingLink
            key={page.slug}
            page={page}
            className="hover:border-red hover:text-red rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors"
          >
            {page.linkLabel}
          </LandingLink>
        ))}
      </div>
    </div>
  )
}
